import {ActionList, Button, Box, Text} from '@primer/react'
import {ShieldIcon, LinkExternalIcon} from '@primer/octicons-react'
import {LABELS} from './constants'
import type {TemplateListVariant} from './TemplateListPane'
import type {ReactNode} from 'react'

type IssueTemplateItemProps = {
  id: string
  onTemplateSelected: () => void
  name: string
  about?: string | null
  link?: string | null
  securityPolicy?: boolean
  variant?: TemplateListVariant
}

type LinkTemplateItemProps = Omit<IssueTemplateItemProps, 'onTemplateSelected'>

type PaneWrapperProps = {
  children: ReactNode
  name: string
  labelId: string
  about?: {
    text: string
    describedById: string
  }
  variant?: TemplateListVariant
}
const PaneWrapper = ({children, name, labelId, about, variant}: PaneWrapperProps): JSX.Element => (
  <Box
    as="li"
    sx={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 3,
      py: 3,
      gap: 2,
      '&:not(:last-of-type)': {
        borderBottom: '1px solid',
        borderColor: 'border.muted',
      },
    }}
  >
    <Box>
      <Box id={labelId} sx={{fontWeight: 500, fontSize: variant === 'full' ? 2 : 1}}>
        {name}
      </Box>
      {about && (
        <Text id={about.describedById} sx={{color: 'fg.muted', fontSize: 0}}>
          {about.text}
        </Text>
      )}
    </Box>
    {children}
  </Box>
)

export const BlankIssueItem = ({
  onTemplateSelected,
  variant,
}: Pick<IssueTemplateItemProps, 'onTemplateSelected' | 'variant'>): JSX.Element => {
  return (
    <IssueTemplateItem
      id="default-blank"
      onTemplateSelected={onTemplateSelected}
      name={LABELS.blankIssueName}
      about={LABELS.blankIssueDescription}
      variant={variant}
    />
  )
}

export const IssueTemplateItem = ({
  id,
  onTemplateSelected,
  name,
  about,
  variant,
}: IssueTemplateItemProps): JSX.Element => {
  const aboutContent = about
    ? {
        text: about,
        describedById: LABELS.templateDescriptionId(id),
      }
    : undefined

  return variant === 'large' || variant === 'full' ? (
    <PaneWrapper key={id} name={name} labelId={LABELS.templateLabelId(id)} about={aboutContent} variant={variant}>
      <Button
        id={LABELS.templateId(id)}
        aria-labelledby={`${LABELS.templateId(id)} ${LABELS.templateLabelId(id)}`}
        aria-describedby={aboutContent?.describedById}
        onClick={onTemplateSelected}
      >
        Choose
      </Button>
    </PaneWrapper>
  ) : (
    <ActionList.Item key={id} onSelect={onTemplateSelected}>
      {name}
      <ActionList.Description variant="block">{about}</ActionList.Description>
    </ActionList.Item>
  )
}

export const SecurityPolicyItem = ({link, variant}: Omit<LinkTemplateItemProps, 'name' | 'id'>): JSX.Element => {
  return (
    <LinkTemplateItem
      id="default-security-policy"
      link={link}
      name={LABELS.securityPolicyName}
      about={LABELS.securityPolicyDescription}
      securityPolicy={true}
      variant={variant}
    />
  )
}

export const LinkTemplateItem = ({
  id,
  name,
  about,
  link,
  securityPolicy,
  variant,
}: LinkTemplateItemProps): JSX.Element => {
  const aboutContent = about
    ? {
        text: about,
        describedById: LABELS.templateDescriptionId(id),
      }
    : undefined

  if (!link) {
    return <></>
  }

  return variant === 'large' || variant === 'full' ? (
    <PaneWrapper key={id} name={name} labelId={LABELS.templateLabelId(id)} about={aboutContent} variant={variant}>
      <Button
        as="a"
        id={LABELS.templateId(id)}
        aria-labelledby={`${LABELS.templateId(id)} ${LABELS.templateLabelId(id)}`}
        aria-describedby={aboutContent?.describedById}
        href={link ?? undefined}
        target={link ? '_blank' : undefined}
        leadingVisual={securityPolicy ? ShieldIcon : LinkExternalIcon}
      >
        Open
      </Button>
    </PaneWrapper>
  ) : (
    <ActionList.LinkItem key={id} href={link ?? undefined} target={link ? '_blank' : undefined}>
      {name}
      <ActionList.Description variant="block">{about}</ActionList.Description>
      <ActionList.TrailingVisual>{securityPolicy ? <ShieldIcon /> : <LinkExternalIcon />}</ActionList.TrailingVisual>
    </ActionList.LinkItem>
  )
}

try{ PaneWrapper.displayName ||= 'PaneWrapper' } catch {}
try{ BlankIssueItem.displayName ||= 'BlankIssueItem' } catch {}
try{ IssueTemplateItem.displayName ||= 'IssueTemplateItem' } catch {}
try{ SecurityPolicyItem.displayName ||= 'SecurityPolicyItem' } catch {}
try{ LinkTemplateItem.displayName ||= 'LinkTemplateItem' } catch {}